import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Search, SlidersHorizontal, Loader2, X, GraduationCap, TrendingUp, Award, FolderGit2, CheckCircle2, Building2 } from "lucide-react";

export default function RecruiterSearch() {
  const [minReadiness, setMinReadiness] = useState(0);
  const [skills, setSkills] = useState("");
  const [gradYear, setGradYear] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/recruiter/candidates", {
      params: { min_readiness: minReadiness || undefined, skills: skills || undefined, grad_year: gradYear || undefined },
    });
    setCandidates(data.candidates);
    setLoading(false);
  }, [minReadiness, skills, gradYear]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="max-w-6xl">
      <p className="eyebrow text-slate-400">RECRUITER PORTAL</p>
      <h1 className="font-display font-extrabold text-3xl tracking-tight text-white mt-1 mb-6">Discover verified candidates</h1>

      <div className="bg-white rounded-2xl p-5 border border-slate-200 mb-6">
        <div className="flex items-center gap-2 text-slate-500 eyebrow mb-3"><SlidersHorizontal size={14} /> FILTERS</div>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div className="relative">
            <label className="text-xs font-medium text-slate-600">Verified skills (comma-separated)</label>
            <Search size={15} className="absolute left-3 top-[30px] text-slate-400" />
            <input data-testid="rec-skills" value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="e.g. CAD, Python"
              className="mt-1 w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 text-slate-900 text-sm focus:border-[#6366F1] outline-none transition-colors" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Graduation year</label>
            <input data-testid="rec-gradyear" value={gradYear} onChange={(e) => setGradYear(e.target.value)} placeholder="e.g. 2027"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 text-slate-900 text-sm focus:border-[#6366F1] outline-none transition-colors" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 font-mono">Min readiness: {minReadiness}%</label>
            <input data-testid="rec-readiness" type="range" min="0" max="100" step="5" value={minReadiness}
              onChange={(e) => setMinReadiness(Number(e.target.value))} className="mt-3 w-full accent-[#6366F1]" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="h-56 flex items-center justify-center"><Loader2 className="animate-spin text-[#6366F1]" /></div>
      ) : candidates.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 border border-dashed border-slate-300 text-center">
          <p className="text-slate-500">No candidates match. Only students who opted in to recruiter visibility appear here.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {candidates.map((c) => (
            <button key={c.user_id} data-testid={`rec-candidate-${c.user_id}`} onClick={() => setSelected(c.user_id)}
              className="text-left bg-white rounded-xl p-5 border border-slate-200 hover:-translate-y-1 transition-transform">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 truncate">{c.full_name}</p>
                  <p className="text-xs text-slate-500 truncate">{c.top_career || "—"}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{c.degree || "—"}{c.grad_year ? ` · Class of ${c.grad_year}` : ""}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="font-mono text-2xl font-bold text-[#6366F1]">{c.best_readiness}%</span>
                  <p className="eyebrow text-slate-400">READINESS</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {c.skills.slice(0, 6).map((s) => (
                  <span key={s} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{s}</span>
                ))}
                {c.skill_count > 6 && <span className="text-[10px] text-slate-400">+{c.skill_count - 6} more</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <CandidateModal id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function CandidateModal({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/recruiter/candidates/${id}`).then(({ data }) => setData(data)); }, [id]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-auto" onClick={(e) => e.stopPropagation()} data-testid="rec-candidate-modal">
        {!data ? (
          <div className="h-64 flex items-center justify-center"><Loader2 className="animate-spin text-[#6366F1]" /></div>
        ) : (
          <div className="p-7">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">{data.profile.full_name}</h2>
                <p className="text-sm text-slate-500">{data.profile.email}</p>
                <p className="text-xs text-slate-400 mt-1">{data.profile.degree || "—"}{data.profile.cgpa ? ` · CGPA ${data.profile.cgpa}` : ""}{data.profile.grad_year ? ` · Class of ${data.profile.grad_year}` : ""}</p>
              </div>
              <button onClick={onClose}><X size={22} className="text-slate-400 hover:text-slate-700" /></button>
            </div>

            {data.profile.education?.length > 0 && (
              <div className="mt-5">
                <div className="flex items-center gap-2 text-slate-600 eyebrow mb-2"><GraduationCap size={14} /> EDUCATION</div>
                <div className="space-y-2">
                  {data.profile.education.map((e, i) => {
                    const meta = [e.institution, e.when, e.cgpa ? `CGPA ${e.cgpa}` : ""].filter(Boolean).join(" · ");
                    return (
                      <div key={i} data-testid={`rec-edu-${i}`}>
                        <p className="text-sm text-slate-800 font-medium">{e.degree}{meta && <span className="text-xs text-slate-400 font-normal"> — {meta}</span>}</p>
                        {e.trend?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {e.trend.map((t, j) => (
                              <span key={j} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">S{t.sem}: {t.grade}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-5">
              <div className="flex items-center gap-2 text-[#4F46E5] eyebrow mb-2"><TrendingUp size={14} /> CAREER READINESS</div>
              <div className="flex flex-wrap gap-2">
                {data.tracks.map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-sm">
                    {t.career_name} <span className="font-mono font-bold text-[#6366F1]">{t.readiness}%</span>
                  </span>
                ))}
                {data.tracks.length === 0 && <span className="text-sm text-slate-400">No tracks.</span>}
              </div>
            </div>

            <div className="mt-5">
              <div className="flex items-center gap-2 text-slate-600 eyebrow mb-2"><CheckCircle2 size={14} className="text-green-600" /> VERIFIED SKILLS</div>
              <div className="flex flex-wrap gap-1.5">
                {data.skills.map((s, i) => (
                  <span key={i} className={`text-[11px] font-medium px-2.5 py-1 rounded-full border ${s.level === "production" ? "bg-green-100 text-green-700 border-green-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>
                    {s.skill_name}
                  </span>
                ))}
                {data.skills.length === 0 && <span className="text-sm text-slate-400">None logged.</span>}
              </div>
            </div>

            {data.projects.length > 0 && (
              <div className="mt-5">
                <div className="flex items-center gap-2 text-slate-600 eyebrow mb-2"><FolderGit2 size={14} /> PROJECTS</div>
                {data.projects.map((p) => <p key={p.id} className="text-sm text-slate-700">• {p.Title}</p>)}
              </div>
            )}

            {data.achievements.length > 0 && (
              <div className="mt-5">
                <div className="flex items-center gap-2 text-slate-600 eyebrow mb-2"><Award size={14} /> ACHIEVEMENTS</div>
                {data.achievements.slice(0, 8).map((e) => (
                  <p key={e.id} className="text-sm text-slate-700">• {e.Title} <span className="text-xs text-slate-400">({e.Activity_Type}, {e.Date_Acquired})</span></p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
